/**
 * The kernel origin of this data plane, as the shell on `<dp>` reads and holds it (kernel apps,
 * WS-16a, design section 3.1, R-013, R-117).
 *
 * The chart writes `window.__ZION_KERNEL_ORIGIN__` into `runtime-config.js` only while the kernel
 * origin is on (WS-5, R-013). The shell reads it once at boot into one of three readings. The form
 * rule is `isKernelOrigin` of `@zion/sdk-contracts`; this module holds no copy of it (R-117). The
 * shell never derives `kernel.` plus the page host.
 */
import { isKernelOrigin } from '@zion/sdk-contracts';

/**
 * A value that passed `isKernelOrigin`: `https://kernel.<host>` with an optional port, and nothing
 * more. Only {@link readKernelOrigin} makes one.
 */
export type KernelOrigin = string & { readonly __brand: 'KernelOrigin' };

/** The one global the reading takes: `runtime-config.js` writes it while the switch is on (WS-5). */
export interface KernelOriginGlobals {
  readonly __ZION_KERNEL_ORIGIN__?: unknown;
}

/**
 * The three readings of the global.
 *
 * - `on`: the global holds a kernel origin; the shell sends sign-in and admin work there.
 * - `off`: the global is absent or the empty string; the shell keeps the sign-in, admin and
 *   passkey paths on `<dp>` (the switch-off arm, OQ-9).
 * - `invalid`: the global holds any other value; the boot shows the fatal screen `kernel-origin`.
 */
export type KernelOriginReading =
  | { readonly kind: 'on'; readonly origin: KernelOrigin }
  | { readonly kind: 'off' }
  | { readonly kind: 'invalid' };

const OFF: KernelOriginReading = Object.freeze({ kind: 'off' });
const INVALID: KernelOriginReading = Object.freeze({ kind: 'invalid' });

/** The brand guard over the contract rule. */
function isBrandedKernelOrigin(value: string): value is KernelOrigin {
  return isKernelOrigin(value);
}

/**
 * The reading of `globals.__ZION_KERNEL_ORIGIN__`.
 *
 * `browserEnvironment` calls it once with `window`. An absent value and the empty string answer
 * `off`. A string that passes `isKernelOrigin` answers `on` with that string. Every other value
 * answers `invalid`: a non-string, `http:`, a path, a query, a fragment, user info, a first label
 * other than `kernel`, a host of fewer than three labels. Pure; never throws.
 */
export function readKernelOrigin(globals: KernelOriginGlobals): KernelOriginReading {
  const value = globals.__ZION_KERNEL_ORIGIN__;
  if (value === undefined || value === '') return OFF;
  return typeof value === 'string' && isBrandedKernelOrigin(value) ? Object.freeze({ kind: 'on', origin: value }) : INVALID;
}

/** The origin the boot set, or `null` for the switch-off arm. */
let active: KernelOrigin | null = null;

/**
 * Set the kernel origin of this document. The boot calls it once, before any route runs: the
 * origin for the `on` reading, `null` for `off` and `invalid`. A test resets it with `null`.
 * The one side effect is one module variable.
 */
export function setActiveKernelOrigin(origin: KernelOrigin | null): void {
  active = origin;
}

/**
 * The kernel origin the boot set, or `null` (the initial value, the switch-off arm). Callers:
 * `AppHost`, `App` and the boot. Reads one module variable.
 */
export function activeKernelOrigin(): KernelOrigin | null {
  return active;
}

/**
 * The absolute URL of `path` and `search` on `origin`.
 *
 * `path` must start with `/`. `search` must be empty or start with `?`. The function joins the three
 * texts, parses the result with `URL` and answers its serialised `href`. A `path` such as
 * `//evil.com/x` stays on the kernel host, because the origin text comes first.
 *
 * Throws `TypeError` for a `path` without a leading `/`, a `search` without a leading `?`, and a
 * result whose origin differs from `origin`. Each one is a caller defect: every caller passes a
 * constant or a `location` value. Invariant: no answer names a host other than the kernel host.
 * Pure.
 */
export function kernelUrl(origin: KernelOrigin, path: string, search = ''): string {
  if (!path.startsWith('/')) throw new TypeError('a kernel path must start with /');
  if (search !== '' && !search.startsWith('?')) throw new TypeError('a kernel search must be empty or start with ?');
  const url = new URL(`${origin}${path}${search}`);
  if (url.origin !== origin) throw new TypeError('the kernel URL left the kernel origin');
  return url.href;
}
